import React, { useRef, useState } from 'react';
import { keyframes } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalAllTypeFileView from './GlobalAllTypeFileView';
import MicIcon from '@mui/icons-material/Mic';
import { postContaboUploads } from '../../redux/features/generalSlice';
import { mbToBytes, UPLOAD_LIMITS } from './utils/uploadSize';
import { getFileType } from './utils/uploadFunctions';

const GlobalAllTypeFileUpload = ({
  formik = null,
  formikNameArray = '',
  dbFolderName = 'general',
  accept = '.pdf,image/*,video/*,audio/*',
  label = 'Attachment',
  previewButtonName,
  removeButtonName,
  updateLabel = 'Update',
  multiple = false,
  mic = false,
  typeSize = {UPLOAD_LIMITS},
}) => {
  const typeSIZE = {...UPLOAD_LIMITS, ...typeSize };
  const fileUploadLoading = useSelector((store) => store.general?.contaboUploading?.loading);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [recording, setRecording] = useState(false);
  const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
`;

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    const mediaRecorder = new MediaRecorder(stream);
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      const audioFile = new File(chunksRef.current, 'record.webm', {
        type: 'audio/webm',
      });
      const uploadedFileName = await uploadFile([audioFile]);
      setUploadedFiles([uploadedFileName[0]?.originalName]);
      formik.setFieldValue(formikNameArray, [uploadedFileName[0]?.file_key]);
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const uploadFile = async (file) => {
    const fileArray = Array.from(file || []);
    const originalNameArray = [];

    try {
      const formData = new FormData();

      fileArray?.forEach((f) => {
        const sizeLimit = mbToBytes(typeSIZE?.[getFileType(f?.name)]);        
        if (sizeLimit >= f?.size) {
          formData.append('files', f);
          originalNameArray.push(f?.name);
        } else {
          throw new Error(`size must be under ${sizeLimit / (1024 * 1024)}MB`);
        }
      });
      formData.append('folderName', dbFolderName);
      const response = await dispatch(postContaboUploads(formData)).unwrap();
      if (response?.status !== 'success' || !response?.data?.length) {
        throw new Error(response?.message || 'File upload failed');
      }

      setUploadedFiles(originalNameArray);

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message || 'File upload failed', {
        variant: 'error',
      });
      throw error;
    }
  };

  const handleRemove = () => {
    if (fileUploadLoading) return;

    formik.setFieldValue(formikNameArray, []);
    setUploadedFiles([]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Upload / Change */}
        <Button
          variant="outlined"
          component="label"
          size="small"
          disabled={fileUploadLoading}
          startIcon={
            fileUploadLoading ? (
              <CircularProgress size={16} />
            ) : Boolean(formik.values[formikNameArray]?.length) ? (
              <SwapHorizIcon fontSize="small" />
            ) : (
              <AttachFileIcon fontSize="small" />
            )
          }
          sx={{
            width: '100%',
            textTransform: 'none',
            borderStyle: 'dashed',
            borderRadius: 2,
            px: 2,
            minHeight: 36,
            fontWeight: 500,
            justifyContent: 'flex-start',
            '&:hover': { borderStyle: 'solid' },
          }}
        >
          {Boolean(formik.values[formikNameArray]?.length) ? updateLabel : label}

          <input
            type="file"
            hidden
            accept={accept}
            capture="environment"
            multiple={multiple}
            disabled={fileUploadLoading}
            onChange={async (e) => {
              const files = e.target.files;
              if (!files?.length) return;

              try {
                const uploadedFileName = await uploadFile(files);
                const attachmentArrayPath = uploadedFileName?.map((e) => e?.file_key);
                formik.setFieldValue(formikNameArray, attachmentArrayPath);
              } finally {
                e.target.value = null;
              }
            }}
          />
        </Button>

        {/* audio recorder */}
        {mic && (
          <>
            <IconButton onClick={recording ? stopRecording : startRecording}>
              <MicIcon
                color={recording ? 'error' : 'action'}
                sx={{
                  animation: recording ? `${pulse} 1s infinite ease-in-out` : 'none',
                }}
              />
            </IconButton>
          </>
        )}

        {/* Preview Button */}
        {Boolean(formik.values?.[formikNameArray]?.length) && (
          <GlobalAllTypeFileView
            attachmentFiles={formik.values[formikNameArray]}
            previewButtonName={previewButtonName}
          />
        )}

        {/* Remove */}
        {Boolean(formik.values?.[formikNameArray]?.length) && (
          <Button size="small" color="error" variant="outlined" onClick={handleRemove}>
            <Icon icon="mdi:remove-bold" width="20" height="18" />
            {removeButtonName || ''}
          </Button>
        )}
      </Box>

      {/* File name */}
      {Boolean(formik.values?.[formikNameArray]?.length && uploadedFiles?.length) &&
        uploadedFiles?.map((name, i) => (
          <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic' }} key={i}>
            {name || ''}
          </Typography>
        ))}
    </Box>
  );
};

export default GlobalAllTypeFileUpload;
